// Pure smart-search parser normaliser. The AI returns a loose JSON object;
// this module normalises whatever we get into a strict SmartSearchFilters shape.

import { ALL_GRADERS, CARD_TYPES, type CardType, type Grader } from "@/lib/grading/companies";

export type SmartSearchFilters = {
  grader: Grader | null;
  grade: string | null;
  cardType: CardType | null;
  game: string | null;
  character: string | null;
  set: string | null;
  language: string | null;
  minPricePence: number | null;
  maxPricePence: number | null;
  keywords: string[];
};

const GRADERS = ALL_GRADERS;

function s(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

function n(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const cleaned = v.replace(/[£$,]/g, "").trim();
    const num = Number(cleaned);
    if (Number.isFinite(num)) return num;
  }
  return null;
}

function poundsToPence(pounds: number | null): number | null {
  if (pounds == null) return null;
  if (pounds > 1_000_000) return Math.round(pounds); // already pence
  return Math.round(pounds * 100);
}

export function normaliseSmartSearch(raw: unknown): SmartSearchFilters {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const graderRaw = s(obj.grader)?.toUpperCase() ?? null;
  const grader = graderRaw && (GRADERS as readonly string[]).includes(graderRaw)
    ? (graderRaw as SmartSearchFilters["grader"])
    : null;

  const kwSource = Array.isArray(obj.keywords) ? obj.keywords : [];
  const seen = new Set<string>();
  const keywords: string[] = [];
  for (const k of kwSource) {
    const str = s(k);
    if (!str) continue;
    const lower = str.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    keywords.push(lower);
    if (keywords.length >= 12) break;
  }

  const cardTypeRaw = s(obj.card_type ?? obj.cardType)?.toLowerCase() ?? null;
  const cardType = cardTypeRaw && (CARD_TYPES as readonly string[]).includes(cardTypeRaw)
    ? (cardTypeRaw as CardType)
    : null;

  return {
    grader,
    grade: s(obj.grade),
    cardType,
    game: s(obj.game)?.toLowerCase() ?? null,
    character: s(obj.character),
    set: s(obj.set),
    language: s(obj.language),
    minPricePence: poundsToPence(n(obj.min_price ?? obj.minPricePounds ?? obj.min)),
    maxPricePence: poundsToPence(n(obj.max_price ?? obj.maxPricePounds ?? obj.max)),
    keywords,
  };
}

/** Turn a filter object into a case-insensitive substring matcher over auction rows. */
export function matchesSmartFilters(
  a: {
    title?: string | null;
    description?: string | null;
    card_set?: string | null;
    grading_company?: string | null;
    grade?: string | null;
    starting_price_pence?: number | null;
    current_bid_pence?: number | null;
    buy_now_price_pence?: number | null;
  },
  filters: SmartSearchFilters,
): boolean {
  const haystack = [a.title, a.description, a.card_set].filter(Boolean).join(" ").toLowerCase();
  if (filters.grader && (a.grading_company ?? "").toUpperCase() !== filters.grader) return false;
  if (filters.grade && String(a.grade ?? "").trim() !== filters.grade.trim()) return false;
  if (filters.character && !haystack.includes(filters.character.toLowerCase())) return false;
  if (filters.set && !haystack.includes(filters.set.toLowerCase())) return false;
  if (filters.language && !haystack.includes(filters.language.toLowerCase())) return false;
  if (filters.game && !haystack.includes(filters.game.toLowerCase())) return false;
  const price = a.buy_now_price_pence ?? a.current_bid_pence ?? a.starting_price_pence ?? 0;
  if (filters.minPricePence != null && price < filters.minPricePence) return false;
  if (filters.maxPricePence != null && price > filters.maxPricePence) return false;
  for (const kw of filters.keywords) {
    if (!haystack.includes(kw)) return false;
  }
  return true;
}
